import { ComponentProps, Fragment, useState } from 'react';

import { Tooltip } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import PersonIcon from '@material-ui/icons/Person';

import {
  RiArrowRightSLine,
  RiCustomerService2Line,
  RiSparklingLine,
  RiVideoLine,
} from '@remixicon/react';

import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  useSidebarPinState,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { IconComponent } from '@backstage/frontend-plugin-api';
import { Text } from '@backstage/ui';

import {
  IconDeviceComputer,
  IconExclamationCircle,
  ModuleIcons,
  extractLastNameComponent,
  useAuth,
  useAuthFactory,
  useFeatureFlag,
  useLocation,
} from '@spotify/confidence-core-react';
import { getTypeOrNull, useAccountMode } from '@spotify/confidence-graphql';
import { chatRouteRef } from '@spotify/plugin-confidence-chat';
import {
  ChatStatusDot,
  useActiveChats,
} from '@spotify/plugin-confidence-chat-react';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';
import { projectChatRouteRef } from '@spotify/plugin-confidence-projects';
import {
  type FavouriteProject,
  useFavouriteProjects,
} from '@spotify/plugin-confidence-projects-react';

import { useUnthread } from '../../hooks/useUnthread';
import { ProfileMenu } from '../ProfileMenu';
import { getMenuLinks, isMenuItem } from './MenuLinks';

const TooltipSidebarItem = ({
  text = '',
  ...props
}: ComponentProps<typeof SidebarItem>) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : text} placement="right" arrow>
      <SidebarItem text={text} {...props} />
    </Tooltip>
  );
};

const NO_SIDEBAR_PAGES = ['/invitation', '/onboarding', '/signup', '/beta'];

const SupportButton = ({ onClick }: { onClick: () => void }) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : 'Support'} placement="right" arrow>
      <SidebarItem
        text="Support"
        icon={RiCustomerService2Line as IconComponent}
        onClick={onClick}
      />
    </Tooltip>
  );
};

const ChevronIcon: IconComponent = () => <RiArrowRightSLine size={20} />;

const ExpandableSidebarProject = ({
  project,
  isActive,
}: {
  project: FavouriteProject;
  isActive: boolean;
}) => {
  const [expanded, setExpanded] = useState(isActive);
  const { chats } = useActiveChats(project.resourceName);
  const chatRoute = useRouteRef(chatRouteRef);
  const projectChatRoute = useRouteRef(projectChatRouteRef);
  const getChatProjectName = (chat: (typeof chats)[number]) =>
    getTypeOrNull(chat.project)?.name;

  const getChatPath = (chat: (typeof chats)[number]) => {
    const chatId = chat.name.split('/').pop()!;
    const chatProjectId = extractLastNameComponent(getChatProjectName(chat));

    if (chatProjectId) {
      return projectChatRoute({ id: chatProjectId, chatId });
    }

    return chatRoute?.({ id: chatId }) ?? `/chats/${chatId}`;
  };

  return (
    <>
      <TooltipSidebarItem
        icon={expanded ? ModuleIcons.projects : ChevronIcon}
        text={project.name}
        to={`/projects/${project.id}`}
        onClick={() => setExpanded(prev => !prev)}
      />
      {expanded && (
        <div style={{ paddingLeft: 44 }}>
          {chats.map(chat => {
            return (
              <div
                key={chat.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 12px 4px 0',
                }}
              >
                <TooltipSidebarItem
                  icon={ChevronIcon}
                  to={getChatPath(chat)}
                  text={chat.displayName ?? chat.name}
                  style={{ flex: 1 }}
                />
                <ChatStatusDot
                  state={chat.state}
                  assistantOutcome={chat.assistantOutcome}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export const SidebarMenu = () => {
  const { user, isAuthenticated } = useAuth();
  const { overrideToken } = useAuthFactory();
  const { isTotalConfidence } = useAccountMode();
  const showEvents = useFeatureFlag('show-events.enabled', false);
  const showInitiatives = useFeatureFlag('initiatives.enabled', false);
  const showExplore = useFeatureFlag('insights-agent.enabled', false);
  const showRecordings = useFeatureFlag('session-replay.enabled', false);
  const isDebugtabEnabled = useFeatureFlag('debugtab.enabled', false);
  const isMeasurementsEnabled = useFeatureFlag('measurements.enabled', false);
  const showProjects = useFeatureFlag('projects.enabled', false);
  const showMeasurements = isDebugtabEnabled && isMeasurementsEnabled;
  const { projects } = useFavouriteProjects();
  const { openChat } = useUnthread();

  const { allowed: canViewInsights } = useCheckResourcePermissions({
    can: 'read',
    name: 'dashboards/global',
  });

  const location = useLocation();
  const pageWithoutSidebar = NO_SIDEBAR_PAGES.some(page =>
    location.pathname.startsWith(page),
  );

  if (!isAuthenticated || pageWithoutSidebar) {
    return null;
  }

  return (
    <Sidebar disableExpandOnHover>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {getMenuLinks({
            isTotalConfidence,
            showEvents,
            showInitiatives,
            showMeasurements,
            showProjects,
          }).map((m, i) => {
            if (isMenuItem(m)) {
              let targetPath = m.to;
              // Highlight Segments when on materialized segments routes
              const isMaterializedSegmentsActive = location.pathname.startsWith(
                '/materializedSegments',
              );
              if (m.to === '/segments' && isMaterializedSegmentsActive) {
                targetPath = location.pathname;
              }

              return (
                <TooltipSidebarItem
                  key={m.to}
                  icon={m.icon ?? ModuleIcons.abtests}
                  to={targetPath}
                  text={m.label}
                />
              );
            }
            return <Fragment key={`menu-item-${i}`}>{m}</Fragment>;
          })}

          {showRecordings && (
            <>
              <SidebarDivider />
              <TooltipSidebarItem
                icon={RiVideoLine as IconComponent}
                to="/recordings/explorer"
                text="Recordings"
              />
            </>
          )}

          {canViewInsights && (
            <>
              <SidebarDivider />
              <TooltipSidebarItem
                icon={ModuleIcons.insights}
                to="/insights"
                text="Insights"
              />
              <SidebarDivider />
            </>
          )}

          {showExplore && (
            <TooltipSidebarItem
              icon={RiSparklingLine as IconComponent}
              to="/explore"
              text="Explore"
            />
          )}

          {showProjects && projects.length > 0 && (
            <>
              <SidebarDivider />
              <Text
                variant="body-small"
                color="secondary"
                weight="bold"
                style={{
                  textTransform: 'uppercase',
                }}
              >
                Favourite projects
              </Text>
              {projects.map(project => (
                <ExpandableSidebarProject
                  key={project.id}
                  project={project}
                  isActive={location.pathname.startsWith(
                    `/projects/${project.id}`,
                  )}
                />
              ))}
            </>
          )}
        </SidebarGroup>
      </div>
      <SidebarGroup label="Settings" icon={<PersonIcon />}>
        <TooltipSidebarItem
          text="Admin"
          to="/admin"
          icon={IconDeviceComputer}
        />
        {(user?.email?.endsWith('@spotify.com') || overrideToken) && (
          <TooltipSidebarItem
            text="Debug"
            to="/debug"
            icon={IconExclamationCircle}
          />
        )}
        <SupportButton
          onClick={() =>
            openChat(
              user?.name && user?.email
                ? { name: user.name, email: user.email }
                : undefined,
            )
          }
        />
        <SidebarDivider />
        <ProfileMenu />
      </SidebarGroup>
    </Sidebar>
  );
};
